import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { meetingApi } from "@/api"
import type { MeetingSessionDetails } from "@/api/types"
import { logger } from "@/lib/logger"
import {
  selectMeetingSessionDetails,
  selectMeetingSessionDetailsLastUpdated,
  setMeetingSessionDetails,
  store,
  useAppSelector,
} from "@/store"

const STALE_THRESHOLD_MS = 10_000
const inflightMeetingSessionDetailsRequests = new Map<
  string,
  Promise<MeetingSessionDetails | null>
>()

interface UseMeetingSessionDetailsOptions {
  sessionId?: string
  staleTime?: number
  forceOnMount?: boolean
  onError?: (error: unknown) => void
}

interface UseMeetingSessionDetailsReturn {
  meetingSessionDetails: MeetingSessionDetails | null
  isLoading: boolean
  refetch: () => Promise<MeetingSessionDetails | null>
  lastUpdated: number | null
}

export const fetchMeetingSessionDetails = async ({
  sessionId,
}: {
  sessionId: string
}): Promise<MeetingSessionDetails> => {
  return meetingApi.getMeetingSessionDetails({ sessionId })
}

const loadMeetingSessionDetails = async ({
  sessionId,
  force = false,
  staleTime = STALE_THRESHOLD_MS,
}: {
  sessionId?: string
  force?: boolean
  staleTime?: number
}): Promise<MeetingSessionDetails | null> => {
  if (!sessionId) return null

  const state = store.getState()
  const lastUpdated = selectMeetingSessionDetailsLastUpdated(sessionId)(state)
  const isStale = force || !lastUpdated || Date.now() - lastUpdated > staleTime

  if (!isStale) {
    return selectMeetingSessionDetails(sessionId)(state)
  }

  const inflightRequest = inflightMeetingSessionDetailsRequests.get(sessionId)
  if (inflightRequest) {
    return inflightRequest
  }

  const request = fetchMeetingSessionDetails({ sessionId })
    .then((meetingSessionDetails) => {
      store.dispatch(setMeetingSessionDetails(meetingSessionDetails))
      return meetingSessionDetails
    })
    .finally(() => {
      inflightMeetingSessionDetailsRequests.delete(sessionId)
    })

  inflightMeetingSessionDetailsRequests.set(sessionId, request)
  return request
}

export const preloadMeetingSessionDetails = async ({
  sessionId,
  force = false,
  staleTime = STALE_THRESHOLD_MS,
}: {
  sessionId?: string
  force?: boolean
  staleTime?: number
}): Promise<MeetingSessionDetails | null> => {
  return loadMeetingSessionDetails({ sessionId, force, staleTime })
}

export const useMeetingSessionDetails = ({
  sessionId,
  staleTime = STALE_THRESHOLD_MS,
  forceOnMount = false,
  onError,
}: UseMeetingSessionDetailsOptions = {}): UseMeetingSessionDetailsReturn => {
  const meetingSessionDetailsSelector = useMemo(
    () => (sessionId ? selectMeetingSessionDetails(sessionId) : () => null),
    [sessionId],
  )
  const lastUpdatedSelector = useMemo(
    () =>
      sessionId
        ? selectMeetingSessionDetailsLastUpdated(sessionId)
        : () => null,
    [sessionId],
  )
  const meetingSessionDetails = useAppSelector(meetingSessionDetailsSelector)
  const lastUpdated = useAppSelector(lastUpdatedSelector)
  const currentSessionIdRef = useRef(sessionId)
  currentSessionIdRef.current = sessionId
  const loadingRequestRef =
    useRef<Promise<MeetingSessionDetails | null> | null>(null)
  const forceFetchedSessionIdRef = useRef<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const fetchDetails =
    useCallback(async (): Promise<MeetingSessionDetails | null> => {
      if (!sessionId) return null
      setIsLoading(true)
      const request = loadMeetingSessionDetails({
        sessionId,
        force: true,
        staleTime,
      })
      loadingRequestRef.current = request
      try {
        return await request
      } catch (err) {
        logger.error("Failed to fetch meeting session details", err)
        if (currentSessionIdRef.current === sessionId) {
          onError?.(err)
        }
        return null
      } finally {
        if (loadingRequestRef.current === request) {
          loadingRequestRef.current = null
          setIsLoading(false)
        }
      }
    }, [onError, sessionId, staleTime])

  useEffect(() => {
    if (!sessionId) {
      forceFetchedSessionIdRef.current = undefined
      return
    }

    const shouldForceFetch =
      forceOnMount && forceFetchedSessionIdRef.current !== sessionId
    if (shouldForceFetch) {
      forceFetchedSessionIdRef.current = sessionId
    }

    const isStale = !lastUpdated || Date.now() - lastUpdated > staleTime
    if (shouldForceFetch || isStale) {
      void fetchDetails()
    }
  }, [fetchDetails, forceOnMount, lastUpdated, sessionId, staleTime])

  return {
    meetingSessionDetails,
    isLoading,
    refetch: fetchDetails,
    lastUpdated,
  }
}
